import * as React from 'react';
import { alpha, useTheme, lighten, darken } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Device from 'react-device-detect';

import {
  GetSpotBookingQuery,
  GetConsumerBookingQuery,
  useUpdateBookingMutation,
  BookingStatusEnum,
} from '@graphql';
import { useSession, useToastError } from '@hooks';
import { handleLineBreak, parseEmojis } from '@utils';
import { getStatusColor } from '@utils';

interface BookingHistoryProps {
  bookingId: string;
  useNote: boolean;
  updates: GetSpotBookingQuery['booking']['updates'] | GetConsumerBookingQuery['booking']['updates'];
}

interface UpdateProps {
  update: BookingHistoryProps['updates'][0] & { isFirst: boolean };
  isMe: boolean;
}

export default function BookingHistory(props: Readonly<BookingHistoryProps>) {
  const { bookingId, updates, useNote } = props;
  const [composerHeight, setComposerHeight] = React.useState(0);

  const splitUpdates = (updates: Readonly<BookingHistoryProps['updates']>) => {
    return updates.reduce(
      (allUpdates, update, updateIndex) => {
        const isFirst = updateIndex === updates.length - 1;
        const { message, status, occasion, startTime, participants, ...rest } = update;

        if (message) {
          // consider message as single update
          allUpdates.push({ ...rest, message, isFirst: false });
        }

        if ([status, occasion, startTime, participants].some((u) => u)) {
          // consider all other updates as single update
          allUpdates.push({ ...rest, status, occasion, startTime, participants, isFirst });
        }
        return allUpdates;
      },
      [] as (BookingHistoryProps['updates'][0] & { isFirst: boolean })[]
    );
  };

  return (
    <Stack
      position="relative"
      flex={1}
      minHeight={0}
      sx={{
        borderBottomLeftRadius: '25px',
        borderBottomRightRadius: '25px',
        overflow: 'hidden',
      }}
    >
      <Stack
        minHeight={0}
        flex={1}
        direction="column-reverse"
        pb={`${composerHeight + 12}px`}
        mb={1.5}
        px={2}
        sx={{
          gap: 0.3,
          overflowY: 'auto',
          borderBottomLeftRadius: '25px',
          borderBottomRightRadius: '25px',
        }}
      >
        {splitUpdates(updates).map((update, idx, array) => (
          <Box
            key={`${update.id}-${idx}`}
            sx={{ pt: idx === array.length - 1 ? (Device.isDesktop ? 2 : 10) : 0 }}
          >
            <DisplayUpdateWrapper update={update} />
          </Box>
        ))}
      </Stack>
      <AddMessage bookingId={bookingId} useNote={useNote} setComposerHeight={setComposerHeight} />
    </Stack>
  );
}

interface AddMessageProps {
  bookingId: string;
  useNote: boolean;
  setComposerHeight: (height: number) => void;
}

function AddMessage(props: Readonly<AddMessageProps>) {
  const { bookingId, useNote, setComposerHeight } = props;
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const toastError = useToastError();
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const borderRadius = '30px';

  const [udpateBooking] = useUpdateBookingMutation({
    refetchQueries: ['GetSpotBooking', 'GetConsumerBooking'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      formik.resetForm();
    },
    onError(error) {
      toastError(error);
    },
  });

  const handleSend = (e: React.KeyboardEvent<HTMLDivElement>) => {
    return handleLineBreak(e, formik.values.message, formik.submitForm);
  };

  const formik = useFormik({
    initialValues: { message: '' },
    onSubmit: async (values) => {
      udpateBooking({
        variables: {
          input: {
            id: bookingId,
            message: values.message,
          },
        },
      });
    },
  });

  React.useLayoutEffect(() => {
    if (!rootRef.current) return;

    const el = rootRef.current;

    const update = () => {
      setComposerHeight(el.offsetHeight);
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, [props]);

  return (
    <Stack
      ref={rootRef}
      position="absolute"
      component="form"
      direction="row"
      left={0}
      bottom={10}
      right={0}
      px={2}
      onSubmit={formik.handleSubmit}
    >
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        autoComplete="off"
        placeholder={t('Write...', { ns: 'conversation' })}
        size="small"
        name="message"
        variant="outlined"
        value={formik.values.message}
        onChange={formik.handleChange}
        onKeyDown={handleSend}
        sx={{
          borderRadius,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(10px)',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'transparent',
              borderRadius,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.grey[800],
              borderRadius,
            },
            '&.Mui-focused fieldset': {
              borderRadius,
              borderColor: theme.palette.grey[800],
              borderWidth: '1px',
            },
          },
          input: {
            color: theme.palette.text.primary,
          },
          textarea: {
            color: theme.palette.text.primary,
            overflowY: 'auto',
            resize: 'none',
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                <IconButton
                  aria-label="send"
                  size="small"
                  type="submit"
                  disabled={!formik.values.message.trim().length}
                  color={!formik.values.message.trim().length ? 'primary' : 'uncolored'}
                  sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#333',
                    },
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                  }}
                >
                  <ArrowUpwardIcon fontSize="small" sx={{ p: 0.05 }} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </Stack>
  );
}

function DisplayUpdateWrapper(props: Readonly<Omit<UpdateProps, 'isMe'>>) {
  const { update } = props;
  const { account } = useSession();
  const avatarSize = Device.isMobileOnly ? 24 : 32;
  const isMe = account?.id === update.author.id;

  return (
    <Stack spacing={1} width="100%">
      <Stack
        direction="row"
        spacing={1}
        width="100%"
        height="100%"
        alignItems="flex-end"
        justifyContent={isMe ? 'flex-end' : 'flex-start'}
      >
        {!isMe && (
          <Tooltip title={update.author.name}>
            <Avatar
              src={update.author.avatar?.url}
              alt={update.author.name}
              sx={{ width: avatarSize, height: avatarSize }}
            />
          </Tooltip>
        )}
        <Stack spacing={2} maxWidth="80%" height="100%">
          {<DisplayUpdate update={update} isMe={isMe} />}
        </Stack>
      </Stack>
    </Stack>
  );
}

function DisplayUpdate(props: Readonly<UpdateProps>) {
  const { update, isMe } = props;
  const theme = useTheme();
  const { t } = useTranslation('booking');
  const isUpdate = !update.isFirst && !update.message;
  let authorName = '';

  if (update.author.__typename === 'Spot') {
    authorName = update.author.name;
  } else if (update.author.__typename === 'Consumer') {
    const { firstname, lastname, deletedAt } = update.author.user;
    authorName = deletedAt ? t(`user.deleted user`, { ns: 'account' }) : `${firstname} ${lastname}.`;
  }

  const parsed = parseEmojis(update.message || '');

  return (
    <Stack spacing={1}>
      <Stack
        spacing={1}
        p={parsed.isOnlyEmojis ? 0 : 1.5}
        width="100%"
        divider={<Divider />}
        sx={{
          backgroundColor: parsed.isOnlyEmojis
            ? 'transparent'
            : isMe
              ? darken(theme.palette.info.dark, 0.5)
              : lighten(theme.palette.background.default, 0.08),
          borderRadius: 3,
        }}
      >
        {update.isFirst && (
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign={isMe ? 'right' : 'left'}
            fontSize={{ xs: '.8rem' }}
          >
            {isMe
              ? t('history.i made a new reservation request')
              : t('history.author made a new reservation request', { author: authorName })}
          </Typography>
        )}
        {isUpdate && (
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign={isMe ? 'right' : 'left'}
            fontSize={{ xs: '.8rem' }}
          >
            {t('history.author updated the reservation', { author: authorName })}
          </Typography>
        )}
        {update.startTime && (
          <Stack direction="row" spacing={10} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color="textSecondary">
              {t('dataGrid.Date')}
            </Typography>
            <Typography variant="body2" fontSize={{ xs: '.8rem' }}>
              {moment(update.startTime).format('LLLL')}
            </Typography>
          </Stack>
        )}
        {update.status && (
          <Stack direction="row" spacing={10} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color="textSecondary">
              {t('dataGrid.Status')}
            </Typography>
            <Chip
              label={
                update.status === BookingStatusEnum.Pending && moment(update.startTime).isBefore(new Date())
                  ? t('status.EXPIRED', { ns: 'booking' })
                  : t(`status.${update.status}`, { ns: 'booking' })
              }
              color={getStatusColor(update.status, update.startTime)}
              size="small"
            />
          </Stack>
        )}
        {update.participants && (
          <Stack direction="row" spacing={10} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color="textSecondary">
              {t('dataGrid.Participants')}
            </Typography>
            <Typography variant="body2" fontSize={{ xs: '.8rem' }}>
              {update.participants || '-'}
            </Typography>
          </Stack>
        )}
        {update.occasion && (
          <Stack direction="row" spacing={10} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color="textSecondary">
              {t('dataGrid.Occasion')}
            </Typography>
            <Typography variant="body2" fontSize={{ xs: '.8rem' }}>
              {t(`occasion.${update.occasion}`)}
            </Typography>
          </Stack>
        )}
        {update.message && (
          <Stack>
            <Typography
              fontSize={parsed.isOnlyEmojis ? (parsed.count <= 8 ? '3.5rem' : '2.5rem') : '1rem'}
              sx={{ whiteSpace: 'pre-line' }}
            >
              {update.message}
            </Typography>
          </Stack>
        )}
      </Stack>
      <DisplayDate {...props} />
    </Stack>
  );
}

function DisplayDate(props: Readonly<UpdateProps>) {
  const { update, isMe } = props;

  return (
    <Stack direction="row" justifyContent={isMe ? 'flex-end' : 'flex-start'} width="100%">
      <Typography variant="caption" color={lighten('#000', 0.3)} fontSize={{ xs: '.7rem' }}>
        {moment().isSame(update.createdAt, 'day')
          ? moment(update.createdAt).format('HH:mm')
          : moment(update.createdAt).fromNow()}
      </Typography>
    </Stack>
  );
}
